import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { queryKeys } from '@/lib/api/keys';
import {
  fetchAttributes,
  fetchCategories,
  fetchFacets,
  fetchProduct,
  fetchProductPage,
  fetchRelated,
  fetchSuggestions,
} from './api';
import type { CatalogFilterState } from './api';

const TAXONOMY_STALE_MS = 30 * 60_000;
const PRODUCT_STALE_MS = 5 * 60_000;

/** The default view: featured first. Callers may pass only the filters they care about. */
const withDefaults = (filters: Partial<CatalogFilterState>): CatalogFilterState => ({
  sort: 'featured',
  ...filters,
});

/** The shop listing as an infinite list: `fetchNextPage` follows the keyset cursor. */
export function useProductPages(
  partial: Partial<CatalogFilterState>,
  options: { limit?: number } = {},
) {
  const filters = withDefaults(partial);
  return useInfiniteQuery({
    queryKey: queryKeys.catalog.products({ ...filters, limit: options.limit }),
    queryFn: ({ pageParam, signal }) => fetchProductPage(filters, pageParam, signal, options.limit),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    // while a new filter set loads, keep showing the previous results (dimmed) instead of a blank page
    placeholderData: keepPreviousData,
  });
}

export function useFacets(partial: Partial<CatalogFilterState>) {
  const { sort: _sort, ...forFacets } = withDefaults(partial);
  return useQuery({
    queryKey: queryKeys.catalog.facets(forFacets),
    queryFn: ({ signal }) => fetchFacets(forFacets, signal),
    placeholderData: keepPreviousData,
  });
}

export function useProduct(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.catalog.product(slug ?? ''),
    queryFn: ({ signal }) => fetchProduct(slug ?? '', signal),
    enabled: Boolean(slug),
    staleTime: PRODUCT_STALE_MS,
  });
}

export function useRelatedProducts(slug: string | undefined) {
  return useQuery({
    queryKey: queryKeys.catalog.related(slug ?? ''),
    queryFn: ({ signal }) => fetchRelated(slug ?? '', signal),
    enabled: Boolean(slug),
    staleTime: PRODUCT_STALE_MS,
  });
}

/** Type-ahead suggestions; nothing is requested for fewer than two characters. */
export function useSuggestions(query: string) {
  const q = query.trim();
  return useQuery({
    queryKey: queryKeys.catalog.suggest(q),
    queryFn: ({ signal }) => fetchSuggestions(q, signal),
    enabled: q.length >= 2,
    staleTime: 60_000,
  });
}

export function useCategories() {
  return useQuery({
    queryKey: queryKeys.catalog.categories(),
    queryFn: ({ signal }) => fetchCategories(signal),
    staleTime: TAXONOMY_STALE_MS,
  });
}

export function useAttributes() {
  return useQuery({
    queryKey: queryKeys.catalog.attributes(),
    queryFn: ({ signal }) => fetchAttributes(signal),
    staleTime: TAXONOMY_STALE_MS,
  });
}

/** A handful of featured stones (home page, carousels). */
export function useFeaturedProducts(limit = 8) {
  return useQuery({
    queryKey: queryKeys.catalog.products({ featured: true, limit, sort: 'featured' }),
    queryFn: ({ signal }) =>
      fetchProductPage({ featured: true, sort: 'featured' }, undefined, signal, limit),
    staleTime: PRODUCT_STALE_MS,
    select: (page) => page.items,
  });
}
